import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Search, Plus, ChevronLeft, ChevronRight, Calendar as CalendarIcon,
  Facebook, Instagram, Linkedin, MessageCircle,
  Download, X, Upload, Loader2, ChevronDown, Clock
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { useAuth } from "../contexts/AuthContext";
import { PhoneInput } from "../components/PhoneInput";
import { CLOSED_STATUSES, daysSince, agingTone, agingLabel, labelForAction } from "../lib/leadActivity";
import { getCountryFlag } from "../lib/phoneFlag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Find the next pending structured follow-up (matching the lead's current status, not done).
 *  Returns { due_date, day, breached } | null
 */
function getNextFollowUp(lead) {
  const fus = Array.isArray(lead?.follow_ups) ? lead.follow_ups : [];
  if (fus.length === 0) return null;
  const todayStr = format(new Date(), "yyyy-MM-dd");
  const pending = fus
    .filter((f) => !f.done_at && f.status === lead.status && f.due_date)
    .sort((a, b) => a.due_date.localeCompare(b.due_date));
  if (pending.length === 0) return null;
  const next = pending[0];
  return { due_date: next.due_date, day: next.day, breached: next.due_date < todayStr };
}

/** Format date as "d Month" (same year) or "d Month yyyy" (different year) */
function formatDate(date) {
  try {
    const d = typeof date === "string" ? new Date(date) : date;
    if (isNaN(d.getTime())) return "";
    const isCurrentYear = d.getFullYear() === new Date().getFullYear();
    return format(d, isCurrentYear ? "d MMMM" : "d MMMM yyyy");
  } catch { return ""; }
}

const sourceIcons = { Facebook, Instagram, LinkedIn: Linkedin, WhatsApp: MessageCircle };
const statusStyles = {
  New: "status-new",
  Contacted: "status-contacted",
  Demoed: "status-qualified",
  Subscribed: "status-converted",
  Lost: "status-lost"
};
const statusColors = {
  New: "bg-blue-50 text-blue-700 hover:bg-blue-100",
  Contacted: "bg-amber-50 text-amber-700 hover:bg-amber-100",
  Demoed: "bg-purple-50 text-purple-700 hover:bg-purple-100",
  Subscribed: "bg-emerald-50 text-emerald-700 hover:bg-emerald-100",
  Lost: "bg-red-50 text-red-700 hover:bg-red-100",
};
const allStatuses = ["New", "Contacted", "Demoed", "Subscribed", "Lost"];

const planBadgeStyles = {
  Free: "bg-gray-100 text-gray-700 border-gray-200",
  Basic: "bg-blue-100 text-blue-700 border-blue-200",
  Plus: "bg-purple-100 text-purple-700 border-purple-200",
  Pro: "bg-amber-100 text-amber-800 border-amber-200",
};

const LeadsList = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { user: currentUser } = useAuth();
  const [leads, setLeads] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [importing, setImporting] = useState(false);

  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [assignedFilter, setAssignedFilter] = useState(currentUser?.role === "cco" ? currentUser.id : "all");
  const [showDupsOnly, setShowDupsOnly] = useState(false);
  const [showStaleOnly, setShowStaleOnly] = useState(false);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", company: "", location: "",
    source: "Facebook", status: "New", notes: "", assigned_to: "",
    follow_up_date: null,
    preferred_language: "ms", interested_plan: null, owner_pic_name: "",
  });

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(), page_size: pageSize.toString(),
        sort_by: "created_at", sort_order: "desc"
      });
      if (search) params.append("search", search);
      if (sourceFilter !== "all") params.append("source", sourceFilter);
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (assignedFilter !== "all") params.append("assigned_to", assignedFilter);
      const response = await axios.get(`${API}/leads?${params}`);
      setLeads(response.data.leads);
      setTotal(response.data.total);
    } catch {
      toast.error("Failed to fetch leads");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, sourceFilter, statusFilter, assignedFilter]);

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API}/team`);
      setTeamMembers(response.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchLeads(); }, [fetchLeads]);
  useEffect(() => { fetchTeamMembers(); }, []);

  const handleSearchChange = (e) => { setSearch(e.target.value); setPage(1); };

  // Apply client-side chip filters on top of the server-fetched list
  const visibleLeads = leads.filter((lead) => {
    if (showDupsOnly) {
      const hasDups = (lead.possible_duplicates?.length || 0) > 0 && lead.custom_fields?._dup_dismissed !== "1";
      if (!hasDups) return false;
    }
    if (showStaleOnly) {
      const CLOSED = ["Subscribed", "Lost"];
      if (CLOSED.includes(lead.status)) return false;
      // Aging: days since last_activity_at (fallback to created_at)
      const ref = lead.last_activity_at || lead.created_at;
      if (!ref) return false;
      const days = Math.floor((Date.now() - new Date(ref).getTime()) / 86400000);
      if (days <= 14) return false;
    }
    return true;
  });

  const dupCount = leads.filter(l => (l.possible_duplicates?.length || 0) > 0 && l.custom_fields?._dup_dismissed !== "1").length;
  const staleCount = leads.filter(l => {
    if (["Subscribed", "Lost"].includes(l.status)) return false;
    const ref = l.last_activity_at || l.created_at;
    if (!ref) return false;
    return Math.floor((Date.now() - new Date(ref).getTime()) / 86400000) > 14;
  }).length;

  const openAddSheet = () => {
    setFormData({ name: "", email: "", phone: "", company: "", location: "", source: "Facebook", status: "New", notes: "", assigned_to: "", follow_up_date: null, preferred_language: "ms", interested_plan: null, owner_pic_name: "" });
    setSheetOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        status: "New",  // new leads always start as New
        follow_up_date: null,  // follow-up schedule is auto-managed once lead becomes Contacted
        assigned_to: formData.assigned_to || null
      };
      await axios.post(`${API}/leads`, data);
      toast.success("Lead created successfully");
      setSheetOpen(false);
      fetchLeads();
    } catch {
      toast.error("Failed to create lead");
    }
  };

  const handleQuickAssign = async (e, leadId, memberId) => {
    e.stopPropagation();
    try {
      await axios.put(`${API}/leads/${leadId}`, { assigned_to: memberId || null });
      const name = memberId ? teamMembers.find(m => m.id === memberId)?.name : "Unassigned";
      toast.success(`Assigned to ${name}`);
      fetchLeads();
    } catch {
      toast.error("Failed to assign lead");
    }
  };

  const handleExcelImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      toast.error("Only .xlsx or .xls files are allowed");
      return;
    }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/leads/import/excel`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.imported} leads (${data.skipped} skipped)`);
      fetchLeads();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to import");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const exportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (sourceFilter !== "all") params.append("source", sourceFilter);
      if (statusFilter !== "all") params.append("status", statusFilter);
      const response = await axios.get(`${API}/leads/export/csv?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'leads_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Leads exported!");
    } catch {
      toast.error("Failed to export leads");
    }
  };

  const totalPages = Math.ceil(total / pageSize);
  const SourceIcon = ({ source }) => { const Icon = sourceIcons[source]; return Icon ? <Icon className="w-4 h-4" /> : null; };

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto animate-fade-in" data-testid="leads-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Leads</h1>
          <p className="text-sm text-gray-500 mt-1">{total.toLocaleString()} total leads in pipeline</p>
        </div>
        <div className="flex gap-2">
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleExcelImport} className="hidden" data-testid="excel-file-input" />
          <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={importing} className="border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg" data-testid="import-excel-btn">
            {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Import Excel
          </Button>
          <Button variant="outline" size="sm" onClick={exportCSV} className="border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg" data-testid="export-leads-btn">
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
          <Button size="sm" onClick={openAddSheet} className="bg-[#0D0D5B] hover:bg-[#1a1a7a] text-white rounded-lg" data-testid="add-lead-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Lead
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-md rounded-2xl mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
                <Input placeholder="Search leads..." value={search} onChange={handleSearchChange} className="pl-10 border-[#E4E4E7] rounded-xl" data-testid="search-input" />
              </div>
            </div>
            <Select value={sourceFilter} onValueChange={(v) => { setSourceFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[150px] border-[#E4E4E7]" data-testid="source-filter"><SelectValue placeholder="All Sources" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                {["Facebook", "Instagram", "LinkedIn", "WhatsApp", "SaleSmartly", "API", "Direct"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[150px] border-[#E4E4E7]" data-testid="status-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {allStatuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={assignedFilter} onValueChange={(v) => { setAssignedFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[180px] border-[#E4E4E7]" data-testid="assigned-filter"><SelectValue placeholder="All Assignees" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Assignees</SelectItem>
                {teamMembers.filter(m => m.assignable !== false).map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {/* Quick-filter chips */}
            <button
              type="button"
              onClick={() => setShowDupsOnly(!showDupsOnly)}
              className={`inline-flex items-center gap-1.5 px-3 h-10 rounded-md text-sm font-medium border transition-colors ${showDupsOnly ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-white border-[#E4E4E7] text-gray-600 hover:bg-gray-50"}`}
              data-testid="filter-dups-chip"
              title="Show only leads flagged as possible duplicates"
            >
              <span className={`w-2 h-2 rounded-full ${showDupsOnly ? "bg-amber-500" : "bg-amber-300"}`} />
              Duplicates {dupCount > 0 && <span className="text-[11px] opacity-70">({dupCount})</span>}
            </button>
            <button
              type="button"
              onClick={() => setShowStaleOnly(!showStaleOnly)}
              className={`inline-flex items-center gap-1.5 px-3 h-10 rounded-md text-sm font-medium border transition-colors ${showStaleOnly ? "bg-orange-50 border-orange-300 text-orange-800" : "bg-white border-[#E4E4E7] text-gray-600 hover:bg-gray-50"}`}
              data-testid="filter-stale-chip"
              title="Show only leads with no activity in 14+ days (excludes Subscribed & Lost)"
            >
              <Clock className="w-3.5 h-3.5" />
              Stale &gt;14d {staleCount > 0 && <span className="text-[11px] opacity-70">({staleCount})</span>}
            </button>
            {(sourceFilter !== "all" || statusFilter !== "all" || assignedFilter !== (currentUser?.role === "cco" ? currentUser.id : "all") || search || showDupsOnly || showStaleOnly) && (
              <Button variant="ghost" onClick={() => { setSearch(""); setSourceFilter("all"); setStatusFilter("all"); setAssignedFilter(currentUser?.role === "cco" ? currentUser.id : "all"); setShowDupsOnly(false); setShowStaleOnly(false); setPage(1); }} className="text-[#52525B]" data-testid="clear-filters-btn">
                <X className="w-4 h-4 mr-2" /> Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-xl rounded-2xl overflow-hidden bg-white">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F9FAFB] hover:bg-[#F9FAFB]">
                <TableHead className="text-xs tracking-[0.1em] uppercase font-semibold text-[#52525B]">Added</TableHead>
                <TableHead className="text-xs tracking-[0.1em] uppercase font-semibold text-[#52525B]">Name</TableHead>
                <TableHead className="text-xs tracking-[0.1em] uppercase font-semibold text-[#52525B]">Contact</TableHead>
                <TableHead className="text-xs tracking-[0.1em] uppercase font-semibold text-[#52525B]">Source</TableHead>
                <TableHead className="text-xs tracking-[0.1em] uppercase font-semibold text-[#52525B] min-w-[260px]">Status</TableHead>
                <TableHead className="text-xs tracking-[0.1em] uppercase font-semibold text-[#52525B]">Assigned</TableHead>
                <TableHead className="text-xs tracking-[0.1em] uppercase font-semibold text-[#52525B]">Next Demo</TableHead>
                <TableHead className="text-xs tracking-[0.1em] uppercase font-semibold text-[#52525B]">Follow Up</TableHead>
                <TableHead className="text-xs tracking-[0.1em] uppercase font-semibold text-[#52525B]">Aging</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}>{Array.from({ length: 9 }).map((_, j) => <TableCell key={j}><div className="h-4 bg-[#F3F4F6] rounded animate-pulse" /></TableCell>)}</TableRow>
                ))
              ) : visibleLeads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-12 text-[#52525B]">No leads found. Click "Add Lead" to create one.</TableCell>
                </TableRow>
              ) : (
                visibleLeads.map((lead) => (
                  <TableRow
                    key={lead.id}
                    className="lead-table-row cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => navigate(`/leads/${lead.id}`)}
                    data-testid={`lead-row-${lead.id}`}
                  >
                    <TableCell className="text-[11px] text-gray-500 whitespace-nowrap">
                      {lead.created_at ? (() => { try { const d = new Date(lead.created_at); return <><div>{format(d, "d MMM")}</div><div className="text-[10px] text-gray-400">{format(d, "h:mm a")}</div></>; } catch { return "-"; } })() : "-"}
                    </TableCell>
                    <TableCell className="font-medium text-[#09090B]">
                      <div className="flex items-center gap-2">
                        {lead.possible_duplicates?.length > 0 && lead.custom_fields?._dup_dismissed !== "1" && (
                          <span
                            className="w-2 h-2 rounded-full bg-amber-400 ring-2 ring-amber-100 shrink-0"
                            title={`Maybe same as ${lead.possible_duplicates.length} other lead${lead.possible_duplicates.length === 1 ? "" : "s"}`}
                            data-testid={`dup-indicator-${lead.id}`}
                          />
                        )}
                        <span className="truncate">{lead.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div className="text-[#09090B]">{lead.email}</div>
                        <div className="text-[#A1A1AA] flex items-center gap-1.5" data-testid={`lead-phone-${lead.id}`}>
                          {getCountryFlag(lead.phone) && (
                            <span className="text-sm leading-none" data-testid={`phone-flag-${lead.id}`} aria-label="country flag">{getCountryFlag(lead.phone)}</span>
                          )}
                          <span>{lead.phone}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <SourceIcon source={lead.source} />
                        <div className="flex flex-col">
                          <span className="text-sm text-[#52525B]">{lead.source}</span>
                          {lead.integration_name && <span className="text-[11px] text-gray-400 leading-tight" data-testid={`integration-name-${lead.id}`}>{lead.integration_name}</span>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="min-w-[260px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium shrink-0 ${statusColors[lead.status] || "bg-gray-100 text-gray-700"}`} data-testid={`status-badge-${lead.id}`}>
                          {lead.status}
                        </span>
                        {lead.status === "Subscribed" && (lead.custom_fields?.Plan || lead.subscription_plan) && (
                          <>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border shrink-0 ${planBadgeStyles[(lead.custom_fields?.Plan || lead.subscription_plan)] || "bg-gray-100 text-gray-600 border-gray-200"}`} data-testid={`plan-badge-${lead.id}`}>
                              {lead.custom_fields?.Plan || lead.subscription_plan}
                            </span>
                            {(() => {
                              const subDate = lead.custom_fields?.["Subscription Date"] || lead.subscribed_at || lead.updated_at;
                              if (!subDate) return null;
                              try {
                                return <span className="text-[10px] text-gray-400 whitespace-nowrap">{format(new Date(subDate), "d MMM yyyy")}</span>;
                              } catch { return null; }
                            })()}
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <button className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium transition-colors bg-gray-50 text-gray-700 hover:bg-gray-100" data-testid={`assign-dropdown-${lead.id}`}>
                            {(() => {
                              const assignee = teamMembers.find(m => m.id === lead.assigned_to);
                              if (!lead.assigned_to || !assignee) {
                                return (
                                  <>
                                    <span className="w-5 h-5 rounded-full bg-gray-200 text-gray-500 inline-flex items-center justify-center text-[9px] font-semibold shrink-0">?</span>
                                    <span>Unassigned</span>
                                  </>
                                );
                              }
                              const initial = (assignee.name || "?").trim().charAt(0).toUpperCase();
                              return (
                                <>
                                  {assignee.avatar ? (
                                    <img src={assignee.avatar} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" />
                                  ) : (
                                    <span className="w-5 h-5 rounded-full bg-[#0D0D5B] text-white inline-flex items-center justify-center text-[9px] font-semibold shrink-0">{initial}</span>
                                  )}
                                  <span>{assignee.name || lead.assigned_name}</span>
                                </>
                              );
                            })()}
                            <ChevronDown className="w-3 h-3 opacity-50" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuItem onClick={(e) => handleQuickAssign(e, lead.id, "")} className={`text-xs ${!lead.assigned_to ? "font-semibold" : ""}`}>
                            Unassigned {!lead.assigned_to && "  (current)"}
                          </DropdownMenuItem>
                          {teamMembers.filter(m => m.assignable !== false).map(m => (
                            <DropdownMenuItem key={m.id} onClick={(e) => handleQuickAssign(e, lead.id, m.id)} disabled={lead.assigned_to === m.id} className={`text-xs ${lead.assigned_to === m.id ? "font-semibold" : ""}`}>
                              {m.name} {lead.assigned_to === m.id && "  (current)"}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                    <TableCell className="text-sm" data-testid={`next-demo-${lead.id}`}>
                      {(() => {
                        const meetings = lead.meetings || [];
                        if (meetings.length === 0) return <span className="text-gray-400">-</span>;
                        const now = new Date();
                        const upcoming = meetings
                          .filter(m => m.start && new Date(m.start) >= now)
                          .sort((a, b) => new Date(a.start) - new Date(b.start))[0];
                        const past = meetings
                          .filter(m => m.start && new Date(m.start) < now)
                          .sort((a, b) => new Date(b.start) - new Date(a.start))[0];
                        const meeting = upcoming || past;
                        if (!meeting) return <span className="text-gray-400">-</span>;
                        const isPast = !upcoming;
                        return (
                          <div className="flex flex-col">
                            <span className={isPast ? "text-gray-500 font-medium" : "text-[#0D0D5B] font-medium"}>
                              {isPast && <span className="text-[10px] uppercase tracking-wide text-gray-400 mr-1">Last:</span>}
                              {formatDate(new Date(meeting.start))}
                            </span>
                            <span className="text-[11px] text-gray-400">{format(new Date(meeting.start), "h:mm a")}</span>
                          </div>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-sm" data-testid={`followup-${lead.id}`}>
                      {(() => {
                        const nf = getNextFollowUp(lead);
                        if (!nf) {
                          return lead.follow_up_date
                            ? <span className="text-[#52525B]">{formatDate(new Date(lead.follow_up_date))}</span>
                            : <span className="text-gray-300">-</span>;
                        }
                        const tone = nf.breached ? "text-red-600 font-semibold" : "text-[#0D0D5B] font-medium";
                        return (
                          <div className="flex flex-col">
                            <span className={tone} data-testid={`followup-date-${lead.id}`}>
                              {formatDate(new Date(nf.due_date))}
                            </span>
                            <span className={`text-[10.5px] ${nf.breached ? "text-red-500" : "text-gray-400"}`}>
                              {nf.breached ? "Overdue" : "Next"} · +{nf.day}d
                            </span>
                          </div>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-sm" data-testid={`aging-${lead.id}`}>
                      {CLOSED_STATUSES.has(lead.status) ? (
                        <span className="text-gray-300">—</span>
                      ) : (() => {
                        const ts = lead.last_activity_at || lead.created_at;
                        const days = daysSince(ts);
                        return (
                          <div className="flex flex-col">
                            <span className={`font-medium ${agingTone(days)}`}>{agingLabel(days)}</span>
                            <span className="text-[10.5px] text-gray-400 truncate max-w-[140px]" title={labelForAction(lead.last_activity_action)}>
                              {labelForAction(lead.last_activity_action) || "—"}
                            </span>
                          </div>
                        );
                      })()}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6 px-2">
          <p className="text-sm text-gray-600">Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, total)} of <span className="font-medium">{total}</span> leads</p>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => setPage(1)} disabled={page === 1} className="border-gray-300 h-9 px-3 rounded-lg text-sm" data-testid="first-page-btn">
              First
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="border-gray-300 h-9 w-9 p-0 rounded-lg" data-testid="prev-page-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) { pageNum = i + 1; }
              else if (page <= 3) { pageNum = i + 1; }
              else if (page >= totalPages - 2) { pageNum = totalPages - 4 + i; }
              else { pageNum = page - 2 + i; }
              return (
                <Button
                  key={pageNum}
                  variant={page === pageNum ? "default" : "outline"}
                  size="sm"
                  onClick={() => setPage(pageNum)}
                  className={`h-9 w-9 p-0 rounded-lg text-sm ${page === pageNum ? "bg-[#0D0D5B] text-white hover:bg-[#1a1a7a]" : "border-gray-300"}`}
                  data-testid={`page-${pageNum}-btn`}
                >
                  {pageNum}
                </Button>
              );
            })}
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="border-gray-300 h-9 w-9 p-0 rounded-lg" data-testid="next-page-btn">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(totalPages)} disabled={page === totalPages} className="border-gray-300 h-9 px-3 rounded-lg text-sm" data-testid="last-page-btn">
              Last
            </Button>
          </div>
        </div>
      )}

      {/* Add Lead Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-[560px] sm:max-w-[560px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-lg font-semibold text-gray-900">Add New Lead</SheetTitle>
            <SheetDescription className="text-sm text-gray-500">Capture workshop details — the same layout as the Lead Detail page</SheetDescription>
          </SheetHeader>
          <form onSubmit={handleSubmit} className="mt-6 space-y-5" data-testid="lead-form">

            {/* ─── Lead Information ─── */}
            <div className="space-y-3">
              <h4 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Lead Information</h4>
              <div className="space-y-2">
                <Label htmlFor="name">Workshop Name *</Label>
                <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required placeholder="e.g. AutoCare Sdn Bhd" className="border-[#E4E4E7]" data-testid="lead-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="border-[#E4E4E7]" data-testid="lead-email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone *</Label>
                  <PhoneInput value={formData.phone} onChange={(val) => setFormData({ ...formData, phone: val })} data-testid="lead-phone-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Preferred Language</Label>
                  <Select value={formData.preferred_language || "ms"} onValueChange={(v) => setFormData({ ...formData, preferred_language: v })}>
                    <SelectTrigger className="border-[#E4E4E7]" data-testid="lead-language-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ms">Bahasa Melayu</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Interested In</Label>
                  <Select value={formData.interested_plan || "none"} onValueChange={(v) => setFormData({ ...formData, interested_plan: v === "none" ? null : v })}>
                    <SelectTrigger className="border-[#E4E4E7]" data-testid="lead-interested-select"><SelectValue placeholder="Pick a plan" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Not specified —</SelectItem>
                      <SelectItem value="Basic">Basic</SelectItem>
                      <SelectItem value="Plus">Plus</SelectItem>
                      <SelectItem value="Pro">Pro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Assigned To</Label>
                <Select value={formData.assigned_to || "unassigned"} onValueChange={(v) => setFormData({ ...formData, assigned_to: v === "unassigned" ? "" : v })}>
                  <SelectTrigger className="border-[#E4E4E7]" data-testid="lead-assigned-select"><SelectValue placeholder="Select team member" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {teamMembers.filter(m => m.assignable !== false).map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* ─── Additional Information ─── */}
            <div className="space-y-3 pt-2 border-t border-gray-100">
              <h4 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Additional Information</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Source *</Label>
                  <Select value={formData.source} onValueChange={(v) => setFormData({ ...formData, source: v })}>
                    <SelectTrigger className="border-[#E4E4E7]" data-testid="lead-source-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Facebook", "Instagram", "LinkedIn", "WhatsApp", "SaleSmartly", "ENGARAGE", "Direct", "Referral", "Other"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Owner / PIC Name</Label>
                  <Input value={formData.owner_pic_name} onChange={(e) => setFormData({ ...formData, owner_pic_name: e.target.value })} placeholder="e.g. Encik Azman" className="border-[#E4E4E7]" data-testid="lead-owner-pic-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Company</Label>
                  <Input value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} className="border-[#E4E4E7]" data-testid="lead-company-input" />
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} className="border-[#E4E4E7]" data-testid="lead-location-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="border-[#E4E4E7] min-h-[90px]" data-testid="lead-notes-input" />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setSheetOpen(false)} className="flex-1 border-gray-300 rounded-lg" data-testid="cancel-lead-btn">Cancel</Button>
              <Button type="submit" className="flex-1 bg-[#0D0D5B] hover:bg-[#1a1a7a] text-white rounded-lg" data-testid="save-lead-btn">Add Lead</Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default LeadsList;
